import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Shield, Users, TrendingUp, MessageCircle, Activity, LogOut, Menu, X,
  ArrowLeftRight, ArrowLeft, Settings,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: Shield, end: true, tid: "admin-nav-dashboard" },
  { to: "/admin/accounts", label: "Comptes", icon: Users, tid: "admin-nav-accounts" },
  { to: "/admin/commercial", label: "Commercial", icon: TrendingUp, tid: "admin-nav-commercial" },
  { to: "/admin/support", label: "Support", icon: MessageCircle, tid: "admin-nav-support" },
  { to: "/admin/technical", label: "Technique", icon: Activity, tid: "admin-nav-technical" },
];

function NavItems({ onClick }) {
  return (
    <nav className="space-y-1">
      {NAV.map(({ to, label, icon: Icon, end, tid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={tid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-colors ${
              isActive
                ? "bg-accent/15 text-accent font-semibold border border-accent/30"
                : "text-white/75 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, memberships, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const roleLabel = user?.platform_role === "founder"
    ? "Fondateur"
    : "Super Administrateur";
  const hasClientAccess = (memberships || []).length > 0;

  const onLogout = async () => {
    await logout();
    toast.success("Déconnexion réussie");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0F1214]">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/10 bg-[#161A1E] px-4 py-6"
        data-testid="admin-sidebar"
      >
        <Link to="/admin" className="flex items-center gap-2 px-2 pb-6 border-b border-white/10">
          <Shield className="h-5 w-5 text-accent" />
          <div>
            <div className="font-serif text-lg text-white leading-tight">Fonbex</div>
            <div className="text-[10px] uppercase tracking-widest text-accent">Back-office</div>
          </div>
        </Link>
        <div className="mt-6 flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <div className="border-t border-white/10 pt-4 space-y-2 text-xs">
          <div className="px-2">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Connecté</div>
            <div className="font-medium text-white truncate mt-0.5">{user?.email}</div>
            <div className="text-accent text-[11px] mt-0.5">{roleLabel}</div>
          </div>
          <Link
            to="/admin/settings"
            data-testid="admin-nav-settings"
            className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
          >
            <Settings className="h-3.5 w-3.5" />
            Mon compte
          </Link>
          {hasClientAccess && (
            <Link
              to="/app/assistant"
              data-testid="admin-nav-switch-client"
              className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              Basculer vers mon espace client
            </Link>
          )}
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
          >
            <LogOut className="h-3.5 w-3.5" />
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 border-b border-white/10 bg-[#161A1E] px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-accent" />
          <span className="text-white font-serif">Back-office</span>
        </Link>
        <button
          type="button"
          className="p-2 text-white"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
          data-testid="admin-mobile-toggle"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
          <aside
            className="absolute left-0 top-0 bottom-0 w-64 bg-[#161A1E] px-4 py-6 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-mobile-sidebar"
          >
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-accent" />
                <span className="text-white font-serif">Back-office</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-white/70 p-1">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 flex-1 overflow-y-auto">
              <NavItems onClick={() => setMobileOpen(false)} />
            </div>
            <div className="border-t border-white/10 pt-4 text-xs">
              <div className="px-2 mb-2">
                <div className="text-white truncate">{user?.email}</div>
                <div className="text-accent text-[11px]">{roleLabel}</div>
              </div>
              <Link
                to="/admin/settings"
                onClick={() => setMobileOpen(false)}
                data-testid="admin-mobile-settings"
                className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
              >
                <Settings className="h-3.5 w-3.5" />
                Mon compte
              </Link>
              {hasClientAccess && (
                <Link
                  to="/app/assistant"
                  onClick={() => setMobileOpen(false)}
                  data-testid="admin-mobile-switch-client"
                  className="flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  Basculer vers mon espace client
                </Link>
              )}
              <button
                type="button"
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-white/70 hover:bg-white/5 hover:text-white text-xs"
                data-testid="admin-mobile-logout"
              >
                <LogOut className="h-3.5 w-3.5" />
                Se déconnecter
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen text-white" data-testid="admin-main">
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
